export * from "react-query-internal";

import {
  useInfiniteQuery as originalUseInfiniteQuery,
  useMutation as originalUseMutation,
  useQueries as originalUseQueries,
  useQuery as originalUseQuery,
} from "react-query-internal";

// The zero-argument overload preserves data inference with v4 placeholderData.
export function keepPreviousData<T>(previousData: T): T;
export function keepPreviousData(): undefined;
export function keepPreviousData<T>(previousData?: T): T | undefined {
  return previousData;
}

type CompatibleOptions = {
  cacheTime?: number;
  gcTime?: number;
  keepPreviousData?: boolean;
  placeholderData?: unknown;
  throwOnError?: unknown;
  useErrorBoundary?: unknown;
};

// Map v5 option names onto v4 before the real hook sees them.
// `initialPageParam` is not mapped: v4 ignores it. First-page fetches still
// come from queryFn defaults (`pageParam = 1`) until the v5 bump.
const adaptOptions = (options: CompatibleOptions): CompatibleOptions => {
  const adaptedOptions = { ...options };

  if (adaptedOptions.placeholderData === keepPreviousData) {
    adaptedOptions.keepPreviousData = true;
    delete adaptedOptions.placeholderData;
  } else if (
    "placeholderData" in adaptedOptions &&
    adaptedOptions.placeholderData === undefined
  ) {
    // Preserve explicit overrides of a wrapper's keepPreviousData default.
    adaptedOptions.keepPreviousData = false;
  }

  if ("gcTime" in adaptedOptions) {
    adaptedOptions.cacheTime = adaptedOptions.gcTime;
    delete adaptedOptions.gcTime;
  }

  if ("throwOnError" in adaptedOptions) {
    adaptedOptions.useErrorBoundary = adaptedOptions.throwOnError;
    delete adaptedOptions.throwOnError;
  }

  return adaptedOptions;
};

// v4 still allows positional overloads (`useQuery(key, fn, options)`). In every
// form the options bag is the last argument; leave keys/fns alone and rewrite
// only that object. Skip non-objects (no options, a queryFn, or a query-key array).
const adaptArguments = (args: unknown[]): unknown[] => {
  const options = args.at(-1);

  if (
    typeof options !== "object" ||
    options === null ||
    Array.isArray(options)
  ) {
    return args;
  }

  return [...args.slice(0, -1), adaptOptions(options)];
};

type LoadingResult = { isLoading: boolean };

// v4 queries have no `isPending`. It matches `isLoading` (`status === "loading"`):
// no data yet, including a disabled query that is not fetching.
// v4.43 mutations already return `isPending`, so `useMutation` is left alone.
//
// Defined as a getter, not spread: `useQuery` results only track the fields a
// component reads, and spreading would read (and re-render on) every field.
const adaptQueryResult = <TResult extends LoadingResult>(result: TResult) =>
  Object.defineProperty(result, "isPending", {
    enumerable: true,
    get: () => result.isLoading,
  });

export const useQuery: typeof originalUseQuery = ((...args: unknown[]) =>
  adaptQueryResult(
    Reflect.apply(originalUseQuery, undefined, adaptArguments(args)),
  )) as typeof originalUseQuery;

export const useInfiniteQuery: typeof originalUseInfiniteQuery = ((
  ...args: unknown[]
) =>
  adaptQueryResult(
    Reflect.apply(originalUseInfiniteQuery, undefined, adaptArguments(args)),
  )) as typeof originalUseInfiniteQuery;

// `useQueries` results are untracked, but they can be the observer's own stored
// result, so copy them instead of adding the getter in place.
export const useQueries: typeof originalUseQueries = ((...args: unknown[]) => {
  const results = Reflect.apply(
    originalUseQueries,
    undefined,
    args,
  ) as LoadingResult[];

  return results.map(result => ({ ...result, isPending: result.isLoading }));
}) as typeof originalUseQueries;

export const useMutation: typeof originalUseMutation = ((...args: unknown[]) =>
  Reflect.apply(
    originalUseMutation,
    undefined,
    adaptArguments(args),
  )) as typeof originalUseMutation;
